/** Bulk "General settings" — origin address, origin protocol, and the
 * compression / query-string toggles. */
import { useMemo, useState } from 'react';
import {
  Input,
  SegmentedControl,
  Switch,
  RadioGroup,
  RadioGroupItem,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '@vnetwork/ui/primitives';
import { useWafDomains, useWafRegions } from '@/api/waf';
import { BulkSection } from './BulkSection';
import { OnOff } from './OnOff';
import { useBulkApply } from './useBulkApply';
import styles from './WafBulkConfigure.module.css';

const PROTOCOLS = [
  { value: 'Match Viewer', label: 'Match viewer' },
  { value: 'HTTP', label: 'HTTP' },
  { value: 'HTTPS', label: 'HTTPS' },
];

/** Split a textarea/CSV of sources into a trimmed, de-duped list. */
const splitSources = (s: string) =>
  Array.from(new Set(s.split(/[\n,]/).map((x) => x.trim()).filter(Boolean)));

export function GeneralSettingsTab({ serviceId, domains }: { serviceId: string; domains: string[] }) {
  // Origin address — Add new (type + sources) OR Clone from another domain.
  const [originMode, setOriginMode] = useState<'new' | 'clone'>('new');
  const [originType, setOriginType] = useState('FQDN');
  const [sources, setSources] = useState('');
  const [copyFrom, setCopyFrom] = useState('');
  const origin = useBulkApply(serviceId, 'origin');
  const originSources = useBulkApply(serviceId, 'origin-sources');
  const domainsQ = useWafDomains(serviceId, { per_page: 200 });
  const allDomains = useMemo(() => (domainsQ.data?.items ?? []).map((d) => d.domain), [domainsQ.data]);

  // Clone-from only applies to IPv4 origins; FQDN is enter-only.
  const canClone = originType === 'IPv4';
  const cloning = canClone && originMode === 'clone';
  const applyOrigin = () => {
    if (cloning) {
      if (copyFrom) originSources.mutate({ domains: domains.filter((d) => d !== copyFrom), copy_from: copyFrom });
    } else {
      origin.mutate({ domains, type: originType, sources: splitSources(sources) });
    }
  };
  const originDisabled = cloning ? !copyFrom : splitSources(sources).length === 0;
  const originPending = origin.isPending || originSources.isPending;

  // Origin protocol
  const [protocol, setProtocol] = useState('Match Viewer');
  const [portHttp, setPortHttp] = useState('80');
  const [portHttps, setPortHttps] = useState('443');
  const [sni, setSni] = useState(false);
  const proto = useBulkApply(serviceId, 'origin-protocol');
  const showHttp = protocol === 'Match Viewer' || protocol === 'HTTP';
  const showHttps = protocol === 'Match Viewer' || protocol === 'HTTPS';

  // WAF region
  const [region, setRegion] = useState('');
  const regionsQ = useWafRegions(serviceId);
  const regions = useMemo(() => regionsQ.data ?? [], [regionsQ.data]);
  const regionMut = useBulkApply(serviceId, 'region');

  // Always use HTTPS (force-HTTPS redirect)
  const [https, setHttps] = useState(false);
  const ssl = useBulkApply(serviceId, 'ssl-redirect');

  // CNAME flatting
  const [flatten, setFlatten] = useState(false);
  const flat = useBulkApply(serviceId, 'cname-flatten');

  return (
    <>
      <BulkSection
        title="Origin address"
        hint="Set the origin on every selected domain — enter it, or clone it from another domain."
        onApply={applyOrigin}
        disabled={originDisabled}
        pending={originPending}
      >
        <div className={styles.inline}>
          <SegmentedControl
            items={[{ value: 'FQDN', label: 'FQDN' }, { value: 'IPv4', label: 'IPv4' }]}
            value={originType}
            onValueChange={setOriginType}
            aria-label="Origin type"
          />
        </div>

        {canClone && (
          <RadioGroup
            value={originMode}
            onValueChange={(v) => setOriginMode(v as 'new' | 'clone')}
            style={{ display: 'flex', flexDirection: 'row', gap: 'var(--vn-space-4)' }}
          >
            <label className={styles.row} style={{ cursor: 'pointer', gap: 6 }}>
              <RadioGroupItem value="new" /> Add new
            </label>
            <label className={styles.row} style={{ cursor: 'pointer', gap: 6 }}>
              <RadioGroupItem value="clone" /> Clone from
            </label>
          </RadioGroup>
        )}

        {cloning ? (
          <Select value={copyFrom} onValueChange={setCopyFrom} disabled={domainsQ.isLoading}>
            <SelectTrigger aria-label="Clone origin from domain">
              <SelectValue placeholder={domainsQ.isLoading ? 'Loading…' : 'Domain'} />
            </SelectTrigger>
            <SelectContent>
              {allDomains.map((d) => (
                <SelectItem key={d} value={d}>{d}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        ) : (
          <Input
            placeholder={originType === 'IPv4' ? '203.0.113.4, 203.0.113.5' : 'www.mysite.com'}
            value={sources}
            onChange={(e) => setSources(e.target.value)}
          />
        )}
      </BulkSection>

      <BulkSection
        title="Origin protocol"
        hint="Edge → origin connection protocol and ports."
        onApply={() =>
          proto.mutate({
            domains,
            ssl_policies: protocol,
            port_http: showHttp ? Number(portHttp) || undefined : undefined,
            port_https: showHttps ? Number(portHttps) || undefined : undefined,
            sni,
          })
        }
        pending={proto.isPending}
      >
        <div className={styles.inline}>
          <SegmentedControl items={PROTOCOLS} value={protocol} onValueChange={setProtocol} aria-label="Origin protocol" />
        </div>
        <div className={styles.row}>
          {showHttp && (
            <label className={styles.row} style={{ gap: 6 }}>
              <span style={{ color: 'var(--vn-fg-muted)', fontSize: 'var(--vn-font-size-sm)' }}>HTTP port</span>
              <Input
                type="number"
                min={1}
                max={65535}
                value={portHttp}
                onChange={(e) => setPortHttp(e.target.value)}
                aria-label="HTTP port"
                style={{ width: 100 }}
              />
            </label>
          )}
          {showHttps && (
            <label className={styles.row} style={{ gap: 6 }}>
              <span style={{ color: 'var(--vn-fg-muted)', fontSize: 'var(--vn-font-size-sm)' }}>HTTPS port</span>
              <Input
                type="number"
                min={1}
                max={65535}
                value={portHttps}
                onChange={(e) => setPortHttps(e.target.value)}
                aria-label="HTTPS port"
                style={{ width: 100 }}
              />
            </label>
          )}
        </div>
        {showHttps && (
          <label className={styles.row} style={{ cursor: 'pointer' }}>
            <Switch checked={sni} onCheckedChange={setSni} aria-label="Send SNI" />
            <span>Send SNI to origin on HTTPS</span>
          </label>
        )}
      </BulkSection>

      <BulkSection
        title="WAF region"
        onApply={() => region && regionMut.mutate({ domains, region })}
        disabled={!region}
        pending={regionMut.isPending}
      >
        <div className={styles.row}>
          <span style={{ color: 'var(--vn-fg-muted)', fontSize: 'var(--vn-font-size-sm)' }}>Overwrite all to</span>
          <Select value={region} onValueChange={setRegion} disabled={regionsQ.isLoading}>
            <SelectTrigger aria-label="WAF region" style={{ minWidth: 200 }}>
              <SelectValue placeholder={regionsQ.isLoading ? 'Loading…' : 'Select a region'} />
            </SelectTrigger>
            <SelectContent>
              {regions.map((r) => (
                <SelectItem key={r.code} value={r.code}>{r.display_name || r.code}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </BulkSection>

      <BulkSection
        title="Always use HTTPS"
        onApply={() => ssl.mutate({ domains, enable: https })}
        pending={ssl.isPending}
      >
        <OnOff value={https} onChange={setHttps} />
      </BulkSection>

      <BulkSection
        title="CNAME flatting"
        hint="Decrease the CNAME resolution time and find the best-performing CDN IP for your domain."
        onApply={() => flat.mutate({ domains, flatten })}
        pending={flat.isPending}
      >
        <OnOff value={flatten} onChange={setFlatten} />
      </BulkSection>
    </>
  );
}

export default GeneralSettingsTab;
